import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { LifeBuoy, Loader2, Send, ChevronLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const CATEGORIES = [
  { value: "bug", label: "Something's broken" },
  { value: "payment", label: "Payments / wallet" },
  { value: "account", label: "My account" },
  { value: "dispute", label: "A dispute" },
  { value: "abuse", label: "Report abuse" },
  { value: "other", label: "Something else" },
];
const STATUS_STYLES: Record<string, string> = {
  open: "bg-amber-50 text-amber-700", in_progress: "bg-blue-50 text-blue-700", resolved: "bg-green-50 text-brand-green",
};
const statusLabel = (s: string) => (s === "in_progress" ? "In progress" : s.charAt(0).toUpperCase() + s.slice(1));

export default function Support() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [tickets, setTickets] = useState<any[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  async function load() { setTickets(await api.myTickets().catch(() => [])); }
  useEffect(() => { if (user) load(); }, [user]);

  if (open) return <TicketThread id={open} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div className="container-x py-8 max-w-2xl">
      <h1 className="text-2xl font-extrabold text-navy flex items-center gap-2 mb-1"><LifeBuoy className="w-6 h-6 text-brand-red" />Support</h1>
      <p className="text-mut text-sm mb-5">Report a problem or ask for help. We'll reply by email and here.</p>

      <NewTicketForm onCreated={(id) => { load(); setOpen(id); }} />

      <h2 className="font-bold text-navy mt-8 mb-3">Your tickets</h2>
      {!tickets ? <div className="text-mut flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        : tickets.length === 0 ? <p className="text-mut text-sm">You haven't opened any tickets yet.</p>
        : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => setOpen(t.id)} className="card p-3 w-full text-left hover:border-navy-500 transition flex items-center justify-between gap-3" data-testid="ticket-row">
                <div className="min-w-0">
                  <div className="font-semibold text-navy truncate">{t.subject}</div>
                  <div className="text-xs text-mut">{new Date(t.createdAt).toLocaleDateString()} · {t.category}</div>
                </div>
                <span className={`chip shrink-0 ${STATUS_STYLES[t.status] || ""}`}>{statusLabel(t.status)}</span>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

function NewTicketForm({ onCreated }: { onCreated: (id: string) => void }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("bug");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api.createTicket({ subject: subject.trim(), category, body: body.trim() });
      setSubject(""); setBody(""); onCreated(r.ticket.id);
    } catch (e: any) { setErr(e?.message || "Could not submit."); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="card p-5 space-y-3" data-testid="support-form">
      <h2 className="font-bold text-navy">Report a problem</h2>
      <div><label className="label">Subject</label><input className="field" required minLength={3} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" /></div>
      <div><label className="label">Category</label>
        <select className="field" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div><label className="label">Details</label><textarea className="field" rows={4} required minLength={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What happened? Include any relevant details." /></div>
      {err && <p className="text-sm text-brand-red">{err}</p>}
      <button type="submit" disabled={busy} className="btn-red disabled:opacity-60" data-testid="support-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Submit ticket</button>
    </form>
  );
}

function TicketThread({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<{ ticket: any; messages: any[] } | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() { setData(await api.ticket(id).catch(() => null)); }
  useEffect(() => { load(); }, [id]);
  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.replyTicket(id, reply.trim()); setReply(""); await load(); }
    catch (e: any) { alert(e?.message || "Could not send."); } finally { setBusy(false); }
  }
  if (!data) return <div className="container-x py-8 text-mut flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  const t = data.ticket;
  return (
    <div className="container-x py-8 max-w-2xl">
      <button onClick={onBack} className="text-sm text-mut hover:text-navy flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" />Back to tickets</button>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="text-xl font-extrabold text-navy">{t.subject}</h1>
        <span className={`chip shrink-0 ${STATUS_STYLES[t.status] || ""}`}>{statusLabel(t.status)}</span>
      </div>
      <p className="text-xs text-mut mb-4">{t.category} · opened {new Date(t.createdAt).toLocaleDateString()}</p>

      {t.outcome && t.status === "resolved" && (
        <div className="card p-3 mb-4 bg-green-50/50 border-brand-green/30">
          <p className="text-sm text-navy flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green mt-0.5" /><span><span className="font-semibold">Resolution: </span>{t.outcome}</span></p>
        </div>
      )}

      <div className="space-y-2" data-testid="ticket-thread">
        {data.messages.map((m) => (
          <div key={m.id} className={`card p-3 ${m.byRole === "admin" ? "bg-navy-50/60" : ""}`}>
            <div className="text-xs text-mut mb-1">{m.byRole === "admin" ? "Support team" : "You"} · {new Date(m.createdAt).toLocaleString()}</div>
            <p className="text-sm text-navy whitespace-pre-line">{m.body}</p>
          </div>
        ))}
      </div>

      {t.status !== "resolved" && (
        <div className="mt-4 flex items-end gap-2">
          <div className="flex-1"><label className="label">Reply</label><textarea className="field" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} /></div>
          <button onClick={send} disabled={busy} className="btn-red h-10 disabled:opacity-60" data-testid="ticket-reply-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send</button>
        </div>
      )}
    </div>
  );
}
